/**
 * Rasio & ukuran keluaran per tujuan.
 *
 * Ukuran piksel ditentukan RASIO saja, bukan platform: 1:1 untuk Pinterest dan
 * 1:1 untuk Instagram adalah berkas yang sama, sehingga satu poster yang
 * ditujukan ke dua platform dengan rasio yang sama cukup dirender sekali.
 *
 * Batas slide dan rasio yang diterima tiap platform suka berubah diam-diam;
 * angka di sini dicek terhadap panduan platform per 2026-11.
 */

export const RATIOS = ['2:3', '4:5', '3:4', '1:1', '9:16'] as const
export type Ratio = (typeof RATIOS)[number]

export const RATIO_SIZE: Record<Ratio, { width: number; height: number }> = {
  '2:3': { width: 1000, height: 1500 },
  '4:5': { width: 1080, height: 1350 },
  '3:4': { width: 1080, height: 1440 },
  '1:1': { width: 1080, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
}

export type ContentKind = 'poster' | 'carousel'
export type Platform = 'pinterest' | 'instagram' | 'tiktok' | 'linkedin'

interface TargetSpec {
  /** Rasio bawaan, yang terpasang begitu tujuan dipilih. */
  default: Ratio
  ratios: readonly Ratio[]
  /** Hanya carousel: batas jumlah slide di platform itu. */
  maxSlides?: number
  /** LinkedIn menerima carousel sebagai dokumen PDF, bukan kumpulan gambar. */
  format: 'image' | 'pdf'
}

export const TARGETS: Record<ContentKind, Partial<Record<Platform, TargetSpec>>> = {
  poster: {
    pinterest: { default: '2:3', ratios: ['2:3', '1:1', '9:16'], format: 'image' },
    instagram: { default: '4:5', ratios: ['4:5', '3:4', '1:1', '9:16'], format: 'image' },
  },
  carousel: {
    // Instagram menerima 20, tapi agent menolak di atas 10 (`CAROUSEL_LIMIT`
    // di agent/src/main/instagram/post.ts) — naikkan keduanya bersamaan.
    instagram: { default: '4:5', ratios: ['4:5', '3:4', '1:1'], maxSlides: 10, format: 'image' },
    tiktok: { default: '9:16', ratios: ['9:16'], maxSlides: 35, format: 'image' },
    linkedin: { default: '4:5', ratios: ['4:5', '1:1'], maxSlides: 20, format: 'pdf' },
  },
}

export interface Target {
  platform: Platform
  ratio: Ratio
}

export function isRatio(v: unknown): v is Ratio {
  return typeof v === 'string' && (RATIOS as readonly string[]).includes(v)
}

/**
 * Periksa tujuan terhadap jenis konten. Mengembalikan pesan error untuk user,
 * atau null kalau sah.
 */
export function targetError(kind: ContentKind, t: Target): string | null {
  const spec = TARGETS[kind][t.platform]
  if (!spec) return `${t.platform} doesn't accept a ${kind}`
  if (!spec.ratios.includes(t.ratio)) {
    return `${t.platform} ${kind}s can't use ${t.ratio}; choose ${spec.ratios.join(', ')}`
  }
  return null
}

/** Rasio unik dari sekumpulan tujuan, urutan pertama kali muncul. */
export function uniqueRatios(targets: readonly Target[]): Ratio[] {
  return [...new Set(targets.map((t) => t.ratio))]
}
